import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Settings,
  ChevronRight,
  LogOut,
  ConciergeBell,
  Image,
  HelpCircle,
  BarChart3,
  AlertTriangle,
  User,
  MessageSquare,
  CreditCard,
  Gift,
  TicketPercent,
  Folder,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useGetProfileQuery, useLogoutMutation } from "@/store";
import { motion } from "framer-motion";

interface NavItem {
  title: string;
  href: string;
  icon: React.ElementType;
}

const navigation: NavItem[] = [
  { title: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { title: "Analytics", href: "/admin/analytics", icon: BarChart3 },
  { title: "Categories", href: "/admin/categories", icon: Folder },
  { title: "Products", href: "/admin/products", icon: Package },
  { title: "Deals", href: "/admin/deals", icon: Gift },
  { title: "Coupons", href: "/admin/coupons", icon: TicketPercent },
  { title: "Stock Management", href: "/admin/stock", icon: AlertTriangle },
  { title: "Orders", href: "/admin/orders", icon: ShoppingCart },
  { title: "Transactions", href: "/admin/transactions", icon: CreditCard },
  { title: "Service Requests", href: "/admin/service-requests", icon: ConciergeBell },
  { title: "FAQ", href: "/admin/faq", icon: HelpCircle },
  { title: "Testimonials", href: "/admin/testimonials", icon: MessageSquare },
  { title: "Newsletter", href: "/admin/newsletter", icon: Mail },
  { title: "Media", href: "/admin/media", icon: Image },
  { title: "Admin Users", href: "/admin/users", icon: Users },
  { title: "Customers", href: "/admin/customers", icon: User },
  { title: "Settings", href: "/admin/settings", icon: Settings },
];

interface AdminSidebarProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
}

export function AdminSidebar({ isMobileMenuOpen, setIsMobileMenuOpen }: AdminSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: profileData } = useGetProfileQuery();
  const [logout] = useLogoutMutation();

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      // Cookie is cleared by backend, just navigate
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout error:", error);
      // Navigate even if logout fails
      navigate("/admin/login");
    }
  };

  const user = profileData?.data;
  const userInitials = user?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "AD";

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex h-full flex-col border-r bg-card/50 backdrop-blur-xl shadow-lg">
          {/* Logo Section - Fixed */}
          <div className="flex h-20 shrink-0 items-center border-b border-border/40">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <img 
                src="https://res.cloudinary.com/dtnerqsid/image/upload/v1769202362/zest-and-zeal/general/logo-white_1769202361916.png" 
                alt="Zest and Zeal" 
                className="h-35 w-60 dark:hidden" 
              />
              <img 
                src="https://res.cloudinary.com/dtnerqsid/image/upload/v1769202372/zest-and-zeal/general/logo-dark_1769202371922.png" 
                alt="Zest and Zeal" 
                className="h-35 w-60 hidden dark:block" 
              />
            </motion.div>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 overflow-y-auto px-6 py-6 scrollbar-hide">
            <ul role="list" className="flex flex-col gap-y-1.5">
              {navigation.map((item, index) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== "/admin" && location.pathname.startsWith(item.href));
                return (
                  <motion.li
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full justify-start gap-3 h-11 px-4 rounded-xl transition-all duration-200",
                        isActive
                          ? "bg-primary text-primary-foreground shadow-md shadow-primary/20 hover:bg-primary hover:text-primary-foreground"
                          : "hover:bg-accent/50 hover:text-accent-foreground text-muted-foreground"
                      )}
                      onClick={() => {
                        navigate(item.href);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <item.icon className={cn(
                        "h-5 w-5 transition-transform duration-200",
                        isActive && "scale-110"
                      )} />
                      <span className="font-medium">{item.title}</span>
                      {isActive && (
                        <ChevronRight className="ml-auto h-4 w-4 opacity-70" />
                      )}
                    </Button>
                  </motion.li>
                );
              })}
            </ul>
          </nav>

          {/* User Section - Fixed at Bottom */}
          <div className="shrink-0 px-6 py-4 border-t border-border/40">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 h-auto p-3 rounded-xl hover:bg-accent/50"
                >
                  <Avatar className="h-9 w-9">
                    {user?.avatarUrl ? (
                      <AvatarImage src={user.avatarUrl} alt={user.name || "Admin"} />
                    ) : null}
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/60 text-primary-foreground font-semibold">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <p className="text-sm font-medium truncate w-full">
                      {user?.name || "Admin User"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate w-full">
                      {user?.email || "admin@zestandzeal.com.ng"}
                    </p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.name || "Admin User"}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email || "admin@zestandzeal.com.ng"}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/admin/profile")}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-card/95 backdrop-blur-xl flex flex-col h-full">
          {/* Logo Section - Fixed */}
          <div className="flex h-20 shrink-0 items-center px-6 border-b border-border/40">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/60 shadow-lg">
                <span className="text-lg font-bold text-primary-foreground">ZZ</span>
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">Zest and Zeal</h1>
                <p className="text-xs text-muted-foreground font-medium">Admin Portal</p>
              </div>
            </div>
          </div>

          {/* Navigation - Scrollable */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            <nav className="flex flex-col gap-y-1.5">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== "/admin" && location.pathname.startsWith(item.href));
                return (
                  <Button
                    key={item.title}
                    variant="ghost"
                    className={cn(
                      "w-full justify-start gap-3 h-11 px-4 rounded-xl",
                      isActive
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "hover:bg-accent/50"
                    )}
                    onClick={() => {
                      navigate(item.href);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.title}</span>
                    {isActive && (
                      <ChevronRight className="ml-auto h-4 w-4 opacity-70" />
                    )}
                  </Button>
                );
              })}
            </nav>
          </div>

          {/* User Section - Fixed at Bottom */}
          <div className="shrink-0 px-4 py-4 border-t border-border/40">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 h-auto p-3 rounded-xl hover:bg-accent/50"
                >
                  <Avatar className="h-9 w-9">
                    {user?.avatarUrl ? (
                      <AvatarImage src={user.avatarUrl} alt={user.name || "Admin"} />
                    ) : null}
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/60 text-primary-foreground font-semibold">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <p className="text-sm font-medium truncate w-full">
                      {user?.name || "Admin User"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate w-full">
                      {user?.email || "admin@zestandzeal.com.ng"}
                    </p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.name || "Admin User"}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email || "admin@zestandzeal.com.ng"}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => {
                  navigate("/admin/profile");
                  setIsMobileMenuOpen(false);
                }}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  navigate("/admin/settings");
                  setIsMobileMenuOpen(false);
                }}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

